import { useEffect, useRef, createRef, useState, ReactElement, cloneElement } from 'react';
import styled, { css } from 'styled-components';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';

import { useMousePosition } from 'shared/hooks/useMousePosition';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { useKeyDown } from 'shared/hooks/useKeyDown';
import { IExternalPicture } from 'shared/types/ExternalPicture';
import { IBuyBarProductVariant } from 'shared/types/BuyBarProductVariant';
import { SwiperCarousel } from 'shared/components/Carousel/SwiperCarousel';
import { OpacityMask } from 'shared/components/Carousel/OpacityMask';
import { ArrowButton } from 'shared/components/Carousel/ArrowButton';
import { ProgressIndicator } from 'shared/components/Carousel/ProgressIndicator';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';

interface ICarouselProps {
  itemList: IExternalPicture[] | { mainPictureUrl: string }[] | IBuyBarProductVariant[];
  carouselItem: ReactElement;
  disableCarouselScroll?: boolean;
  centeredViewport?: boolean;
  keyDownIgnoreFocus?: boolean;
  lazyLoad?: boolean;
  showOpacityMask?: boolean;
  showProgressIndicator?: boolean;
  showArrowButtons?: boolean;
}

const arrowButtonStyle = {
  width: '120px',
  height: 'calc(100vh - 182px)',
  position: 'fixed',
  padding: '0 40px',
};

export const Carousel = ({
  itemList = [],
  carouselItem,
  disableCarouselScroll = false,
  showOpacityMask = false,
  centeredViewport = false,
  keyDownIgnoreFocus = false,
  lazyLoad,
  showProgressIndicator = false,
  showArrowButtons = false,
}: ICarouselProps) => {
  const { slug } = useStoreSelector((state) => state.pdp.productTemplate);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [anchorPoints, setAnchorPoints] = useState<number[]>([]);
  const [itemListRefs, setItemListRefs] = useState([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [displayAfterMouseMove, setDisplayAfterMouseMove] = useState(true);
  const carouselWrapperRef = useRef<HTMLDivElement>(null);
  const mousePosition = useMousePosition(10);
  const lastIndex = itemList.length - 1;
  const {
    windowSize: { width: windowWidth },
  } = useWindowSize();
  const screenCenter = windowWidth / 2;

  let imageLoadCounter = 0;
  const handleImageLoad = () => {
    imageLoadCounter += 1;
    if (imageLoadCounter === itemList.length) {
      setImagesLoaded(true);
    }
  };

  useEffect(() => {
    const id = setTimeout(() => {
      setDisplayAfterMouseMove(false);
    }, 3000);
    return () => {
      setDisplayAfterMouseMove(true);
      clearInterval(id);
    };
  }, [mousePosition]);

  /*
  IntersectionObserver is used here to calculate the center point of each image
  without triggering reflow. This approach is based on this article:
  https://toruskit.com/blog/how-to-get-element-bounds-without-reflow/#use-intersectionobserver-to-get-element-bounds.
  */
  const setItemAnchorPoints = (refs) => {
    const observer = new IntersectionObserver((entries) => {
      const elementCenters = [0];

      for (const [idx, entry] of entries.entries()) {
        const { x, width } = entry.boundingClientRect;
        const center = x + width / 2 - screenCenter;

        if (idx > 0 && center > 0) {
          elementCenters.push(center);
        }
      }

      setAnchorPoints(elementCenters);
      observer.disconnect();
    });

    for (const ref of refs) {
      observer.observe(ref?.current);
    }
  };

  useEffect(() => {
    if (!centeredViewport) {
      const itemRefs = itemList.map(() => createRef());
      setItemListRefs(itemRefs);
    }
  }, [itemList]);

  useEffect(() => {
    if (itemListRefs?.length > 0 && !centeredViewport && imagesLoaded) {
      setItemAnchorPoints(itemListRefs);
    }
  }, [itemListRefs, imagesLoaded]);

  const move = (direction) => {
    if (direction === 'left' && activeIndex === 0) {
      carouselWrapperRef?.current?.scrollTo({
        left: anchorPoints[activeIndex],
        behavior: 'smooth',
      });
      return;
    }

    if (direction === 'right' && activeIndex === lastIndex) {
      carouselWrapperRef?.current?.scrollTo({
        left: anchorPoints[lastIndex],
        behavior: 'smooth',
      });
      return;
    }

    carouselWrapperRef?.current?.scrollTo({
      left: anchorPoints[direction === 'right' ? activeIndex + 1 : activeIndex - 1],
      behavior: 'smooth',
    });

    setActiveIndex(direction === 'right' ? activeIndex + 1 : activeIndex - 1);
  };

  let lastScroll = 0 || carouselWrapperRef?.current?.scrollLeft;

  const handleScroll = debounce((e) => {
    const {
      scrollLeft,
      offsetWidth,
      scrollWidth,
    }: { scrollLeft: number; offsetWidth: number; scrollWidth: number } = e.target;
    const currentScroll = carouselWrapperRef?.current?.scrollLeft;
    const direction = currentScroll > lastScroll ? 'right' : 'left';

    lastScroll = currentScroll <= 0 ? 0 : currentScroll;

    if (direction === 'left' && currentScroll === 0) {
      setActiveIndex(0);
    } else if (direction === 'right' && scrollLeft + offsetWidth === scrollWidth) {
      setActiveIndex(lastIndex);
    } else if (direction === 'right' && currentScroll > anchorPoints[activeIndex + 1]) {
      setActiveIndex(activeIndex + 1);
    } else if (direction === 'left' && currentScroll < anchorPoints[activeIndex - 1]) {
      setActiveIndex(activeIndex - 1);
    }
  }, 10);

  const handleArrowClick = (direction) => {
    if (centeredViewport) {
      return;
    }
    move(direction);
  };

  const handleKeyDown = (direction) => {
    if (
      disableCarouselScroll ||
      (!keyDownIgnoreFocus && document && document.activeElement !== carouselWrapperRef?.current)
    ) {
      return;
    }
    move(direction);
  };

  useKeyDown('ArrowRight', (e) => {
    e.preventDefault();
    handleKeyDown('right');
  });

  useKeyDown('ArrowLeft', (e) => {
    e.preventDefault();
    handleKeyDown('left');
  });

  useEffect(() => {
    if (activeIndex !== 0) {
      const list = itemList as { mainPictureUrl: string }[];
      sendTrackingEvent('PDP_IMAGE_VIEW', {
        product_template_slug: slug,
        picture_id: list[activeIndex]?.mainPictureUrl,
      });
    }
  }, [activeIndex]);

  const handleSlideChange = (swipercore) => {
    const { activeIndex: idx } = swipercore;
    setActiveIndex(idx);
  };

  // No items to display. Trigger placeholder.
  if (itemList.length === 0) {
    itemList = [{ mainPictureUrl: 'missing.png' }];
  }

  return (
    <Carousel.Wrapper
      ref={carouselWrapperRef}
      onScroll={centeredViewport ? noop : handleScroll}
      tabIndex={0}
      centeredViewport={centeredViewport}
    >
      {showOpacityMask && <OpacityMask data-qa="opacity_mask" side="left" />}
      <ArrowButton
        className={centeredViewport ? 'prev' : ''}
        direction="left"
        onArrowClick={handleArrowClick}
        style={arrowButtonStyle}
        show={
          showArrowButtons &&
          !disableCarouselScroll &&
          mousePosition.x < screenCenter &&
          (centeredViewport ? activeIndex !== 0 : carouselWrapperRef?.current?.scrollLeft !== 0)
        }
      />
      {centeredViewport ? (
        <SwiperCarousel
          allowSlideNext={!disableCarouselScroll}
          allowSlidePrev={!disableCarouselScroll}
          allowTouchMove={!disableCarouselScroll}
          carouselItem={carouselItem}
          carouselRef={carouselWrapperRef}
          centeredSlides
          centeredSlidesBounds
          centeredViewport={centeredViewport}
          disableCarouselScroll={disableCarouselScroll}
          freeMode
          itemList={itemList}
          keyDownIgnoreFocus
          lazyLoad={lazyLoad}
          mousewheel={{
            forceToAxis: true,
          }}
          navigation={{
            prevEl: '.prev',
            nextEl: '.next',
          }}
          onSlideChange={handleSlideChange}
          slidesPerView={1}
        />
      ) : (
        itemList.map((item, idx) => (
          // TODO: better solution for key
          <Carousel.ItemWrapper
            data-qa="desktop_carousel_image_wrapper"
            key={item.mainPictureUrl}
            ref={itemListRefs[idx]}
          >
            {cloneElement(carouselItem, {
              item,
              idx,
              itemList,
              listLength: itemList.length,
              centeredViewport,
              handleImageLoad,
            })}
          </Carousel.ItemWrapper>
        ))
      )}
      <ArrowButton
        className={centeredViewport ? 'next' : ''}
        direction="right"
        onArrowClick={handleArrowClick}
        style={arrowButtonStyle}
        show={
          showArrowButtons &&
          !disableCarouselScroll &&
          mousePosition.x > screenCenter &&
          activeIndex !== lastIndex
        }
      />
      {showOpacityMask && <OpacityMask data-qa="opacity_mask" side="right" />}
      {showProgressIndicator && (
        <Carousel.ProgressIndicator
          activeIndex={activeIndex + 1}
          itemListLength={itemList.length}
          $displayAfterMouseMove={displayAfterMouseMove}
        />
      )}
    </Carousel.Wrapper>
  );
};

const centeredView = css`
  overflow: hidden;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const leftAlignedView = css`
  overflow: scroll;
  align-items: center;
  justify-content: space-between;
`;

Carousel.Wrapper = styled.div<{ centeredViewport: boolean }>`
  display: flex;
  height: 100%;
  position: relative;
  padding-top: 35px;
  padding-bottom: 35px;
  ${hideScrollbars};
  ${({ centeredViewport }) => (centeredViewport ? centeredView : leftAlignedView)}
  .swiper-slide {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }
`;

Carousel.Viewport = styled.div`
  overflow: hidden;
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-start;
`;

Carousel.ItemWrapper = styled.div`
  @media (max-height: 750px) {
    height: 100%;
  }
  position: relative;
  flex: 1;
  transition: transform 1s ease-in-out;
`;

Carousel.ProgressIndicator = styled(ProgressIndicator)<{ $displayAfterMouseMove: boolean }>`
  ${({ $displayAfterMouseMove }) => ($displayAfterMouseMove ? `opacity: 1` : `opacity: 0`)};
  transition: opacity 0.5s ease-in-out;
`;
